import {
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { classNames } from './classNames';

const VIEWPORT_GAP = 8;
const CURSOR_GAP = 14;

export interface HabitaTooltipPoint {
  x: number;
  y: number;
}

export interface HabitaChartTooltipPayloadItem {
  color?: string;
  dataKey?: number | string;
  name?: string;
  value?: number | string;
}

export function HabitaChartTooltipSurface({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={classNames(
        'isolate rounded-md border border-border-subtle bg-card px-3 py-2 font-instrument text-xs text-textPrimary shadow-lg shadow-slate-900/10',
        className
      )}
    >
      {children}
    </div>
  );
}

export function HabitaRechartsTooltip({
  active,
  label,
  labelFormatter,
  nameFormatter,
  payload,
  valueFormatter,
}: {
  active?: boolean;
  label?: ReactNode;
  labelFormatter?: (label: ReactNode) => ReactNode;
  nameFormatter?: (name: string, item: HabitaChartTooltipPayloadItem) => ReactNode;
  payload?: HabitaChartTooltipPayloadItem[];
  valueFormatter?: (value: number | string, item: HabitaChartTooltipPayloadItem) => ReactNode;
}) {
  const visiblePayload = payload?.filter((item) => item.value !== undefined && item.value !== null) ?? [];
  if (!active || visiblePayload.length === 0) return null;

  const formattedLabel = labelFormatter ? labelFormatter(label) : label;

  return (
    <HabitaChartTooltipSurface className="min-w-40">
      {formattedLabel !== undefined && formattedLabel !== null && formattedLabel !== '' && (
        <p className="mb-1.5 font-semibold">{formattedLabel}</p>
      )}
      <div className="space-y-1">
        {visiblePayload.map((item, index) => {
          const name = item.name ?? String(item.dataKey ?? 'Valor');
          return (
            <p
              key={`${String(item.dataKey ?? name)}-${index}`}
              className="flex items-center justify-between gap-4"
            >
              <span className="inline-flex min-w-0 items-center gap-2 text-textSecondary">
                <span
                  aria-hidden
                  className="h-2 w-2 shrink-0 rounded-full"
                  style={{ backgroundColor: item.color ?? 'hsl(var(--primary))' }}
                />
                <span className="truncate">{nameFormatter ? nameFormatter(name, item) : name}</span>
              </span>
              <span className="shrink-0 font-mono font-semibold text-textPrimary">
                {valueFormatter ? valueFormatter(item.value as number | string, item) : item.value}
              </span>
            </p>
          );
        })}
      </div>
    </HabitaChartTooltipSurface>
  );
}

export function HabitaFloatingTooltip({
  children,
  className,
  point,
}: {
  children: ReactNode;
  className?: string;
  point: HabitaTooltipPoint;
}) {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState({ left: point.x + CURSOR_GAP, top: point.y + CURSOR_GAP });

  useLayoutEffect(() => {
    const tooltip = tooltipRef.current;
    if (!tooltip) return;

    const bounds = tooltip.getBoundingClientRect();
    let left = point.x + CURSOR_GAP;
    let top = point.y + CURSOR_GAP;

    if (left + bounds.width + VIEWPORT_GAP > window.innerWidth) {
      left = point.x - bounds.width - CURSOR_GAP;
    }
    if (top + bounds.height + VIEWPORT_GAP > window.innerHeight) {
      top = point.y - bounds.height - CURSOR_GAP;
    }

    left = Math.max(VIEWPORT_GAP, Math.min(left, window.innerWidth - bounds.width - VIEWPORT_GAP));
    top = Math.max(VIEWPORT_GAP, Math.min(top, window.innerHeight - bounds.height - VIEWPORT_GAP));

    setPosition({ left, top });
  }, [children, point.x, point.y]);

  if (typeof document === 'undefined') return null;

  return createPortal(
    <div
      ref={tooltipRef}
      role="tooltip"
      className={classNames('pointer-events-none fixed z-[300] max-w-[min(20rem,calc(100vw-1rem))]', className)}
      style={position}
    >
      {children}
    </div>,
    document.body
  );
}
